import { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  X, Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Copy, ChevronRight,
  ChevronLeft, Loader2, Users, ShieldAlert, Download,
} from "lucide-react";

/**
 * Bulk import wizard for trial users. 4 steps:
 *   1. Upload         — pick CSV/XLSX, download template
 *   2. Review & Fix   — table w/ status badges, inline edit
 *   3. Options        — tag, notes, dedupe policy
 *   4. Import         — commit + summary
 *
 * The modal ONLY writes to DB when the user clicks Import on step 4. Every
 * earlier action is a dry-run against /user-import/validate.
 */
const REQUIRED_COLS = ["name", "email", "phone", "exam"];
const ALL_COLS = [
  "name", "email", "phone", "exam",
  "country", "state", "city",
  "join_date", "last_login",
  "subscription_status", "external_id", "tags", "notes",
];

const STATUS_META = {
  ready:      { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Ready" },
  needs_edit: { icon: AlertCircle,  cls: "bg-amber-50 text-amber-800 border-amber-200",       label: "Needs fix" },
  duplicate:  { icon: Copy,         cls: "bg-blue-50 text-blue-700 border-blue-200",          label: "Duplicate" },
  error:      { icon: ShieldAlert,  cls: "bg-red-50 text-red-700 border-red-200",             label: "Error" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.error;
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${m.cls}`}>
      <Icon className="h-3 w-3" /> {m.label}
    </span>
  );
}

/** Parse an uploaded file (CSV or XLSX) into a normalized [{col: value, ...}] array. */
function parseFile(file) {
  return new Promise((resolve, reject) => {
    const name = file.name.toLowerCase();
    if (name.endsWith(".csv")) {
      Papa.parse(file, {
        header: true, skipEmptyLines: true, transformHeader: (h) => h.trim().toLowerCase(),
        complete: (res) => resolve(res.data),
        error: reject,
      });
      return;
    }
    if (name.endsWith(".xlsx") || name.endsWith(".xls")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const wb = XLSX.read(e.target.result, { type: "array" });
          const sheet = wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false });
          // Normalize header casing/whitespace
          const cleaned = rows.map((r) => {
            const out = {};
            Object.keys(r).forEach((k) => { out[String(k).trim().toLowerCase()] = r[k]; });
            return out;
          });
          resolve(cleaned);
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
      return;
    }
    reject(new Error("Only CSV / XLSX files are supported."));
  });
}

/**
 * Column aliases — map ANY common export header (from PrepHacker's own DB
 * export, from CRM systems, or from ad-hoc spreadsheets) to our canonical
 * schema. Keys are canonical, values are the list of accepted aliases (already
 * lowercased). First match wins.
 */
const COLUMN_ALIASES = {
  name:                ["name", "full_name", "fullname", "user_name", "username"],
  email:               ["email", "email_address", "email_id", "mail"],
  phone:               ["phone", "phone_no", "phone_number", "mobile", "mobile_no", "mobile_number", "contact", "contact_no", "whatsapp", "whatsapp_no"],
  exam:                ["exam", "targeting_exam", "target_exam", "exam_name", "course"],
  country:             ["country"],
  state:               ["state", "province", "region"],
  city:                ["city", "town"],
  join_date:           ["join_date", "joining_date", "joined_at", "joined_on", "signup_date", "created_at", "registration_date"],
  last_login:          ["last_login", "last_seen", "last_active", "last_login_at"],
  subscription_status: ["subscription_status", "is_subscription", "subscription", "subscribed", "plan_status"],
  external_id:         ["external_id", "user_code", "user_id", "id", "customer_id"],
  tags:                ["tags", "tag", "labels"],
  notes:               ["notes", "note", "comment", "comments", "remarks"],
  // v29.2 — deletion signal now passed through to backend so the deleted-user
  // pipeline (DND + subscription_status="deleted" + Deleted Users group) fires.
  is_delete:           ["is_delete", "is_deleted", "deleted"],
  delete_reason:       ["delete_reason", "deletion_reason", "delete_note"],
};

/** Pick the first non-empty value across a list of alias keys. */
function pickAlias(rawRow, aliases) {
  for (const k of aliases) {
    const v = rawRow[k];
    if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
  }
  return "";
}

/** Normalize free-form subscription status values into our 4 canonical states. */
function normalizeSubStatus(raw) {
  if (!raw) return "";
  const s = String(raw).trim().toLowerCase();
  if (["", "n/a", "na", "none", "no", "false", "0", "trial", "free"].includes(s)) return "trial";
  if (["yes", "true", "1", "paid", "active", "premium", "subscribed"].includes(s)) return "paid";
  if (["expired", "lapsed", "ended"].includes(s)) return "expired";
  if (["cancelled", "canceled", "churned", "refunded"].includes(s)) return "cancelled";
  return s; // let the backend validator flag anything unknown
}

/** Is this cell effectively "deleted / soft-deleted"? Handles Yes/1/true/etc. */
function isDeletedFlag(v) {
  if (v === undefined || v === null) return false;
  const s = String(v).trim().toLowerCase();
  return ["yes", "true", "1", "y", "deleted"].includes(s);
}

// Any column name we already handle via COLUMN_ALIASES + first/last_name +
// exam-scoring detection. Everything else in the file is "ignored" and gets
// listed for the operator's awareness.
const KNOWN_COLUMN_KEYS = new Set([
  "name", "full_name", "fullname", "user_name", "username",
  "first_name", "firstname", "last_name", "lastname", "surname",
  "email", "email_address", "email_id", "mail",
  "phone", "phone_no", "phone_number", "mobile", "mobile_no", "mobile_number",
  "contact", "contact_no", "whatsapp", "whatsapp_no",
  "exam", "targeting_exam", "target_exam", "exam_name", "course",
  "country", "state", "province", "region", "city", "town",
  "join_date", "joining_date", "joined_at", "joined_on", "signup_date", "created_at", "registration_date",
  "last_login", "last_seen", "last_active", "last_login_at",
  "subscription_status", "is_subscription", "subscription", "subscribed", "plan_status",
  "external_id", "user_code", "user_id", "id", "customer_id",
  "tags", "tag", "labels",
  "notes", "note", "comment", "comments", "remarks",
  "is_delete", "delete_reason",  // consumed as row-skip signal, not persisted
]);

/**
 * From a raw row + list of raw column names, detect which exam this user is
 * "active" in. Convention: score-tracker columns have a header equal to the
 * exam name and a value like ``"Score: 12/ 25 | Attempts Que: ..."``. We pick
 * the exam column whose numerator score is > 10.
 * Returns { exam, ignoredScoreColumns[] } where ignoredScoreColumns lists any
 * score-shaped columns we found (helpful for the "not imported" panel).
 */
function detectExamFromScores(rawRow, allColumnKeys) {
  const scoreColumns = allColumnKeys.filter((k) => {
    const v = rawRow[k];
    return typeof v === "string" && /score\s*:\s*\d+\s*\/\s*\d+/i.test(v);
  });
  if (scoreColumns.length === 0) return { exam: "", ignoredScoreColumns: [] };
  let best = { name: "", num: -1 };
  scoreColumns.forEach((k) => {
    const m = String(rawRow[k]).match(/score\s*:\s*(\d+)\s*\/\s*\d+/i);
    if (m) {
      const num = parseInt(m[1], 10);
      if (num > best.num) best = { name: k, num };
    }
  });
  // Only claim the exam if the top score crosses the > 10 threshold.
  const exam = best.num > 10 ? best.name : "";
  return { exam, ignoredScoreColumns: scoreColumns };
}

/** Coerce a parsed row (arbitrary keys) into our canonical schema. */
function toImportRow(rawRow, allColumnKeys = []) {
  const out = {};
  Object.entries(COLUMN_ALIASES).forEach(([canonical, aliases]) => {
    out[canonical] = pickAlias(rawRow, aliases);
  });

  // Special-case: build `name` from first_name + last_name when there's no
  // pre-combined name column.
  if (!out.name) {
    const fn = String(rawRow.first_name ?? rawRow.firstname ?? "").trim();
    const ln = String(rawRow.last_name ?? rawRow.lastname ?? rawRow.surname ?? "").trim();
    const combined = `${fn} ${ln}`.trim();
    if (combined) out.name = combined;
  }

  // Exam detection: if the primary `exam` value is empty / "N/A", try to
  // infer the active exam from performance-score columns whose header is the
  // exam name (e.g. "NCLEX - RN": "Score: 12/25 | ...").
  const examBlank = !out.exam || ["n/a", "na", "none", "unknown", ""].includes(out.exam.toLowerCase());
  if (examBlank) {
    const { exam } = detectExamFromScores(rawRow, allColumnKeys);
    if (exam) out.exam = exam;
  }

  // Subscription-status coercion: turn "N/A" / "Yes" / "1" / etc. into
  // canonical values. The backend will further auto-classify trial→expired
  // based on the 3-day rule against `join_date`.
  out.subscription_status = normalizeSubStatus(out.subscription_status) || "trial";

  return out;
}

export default function ImportUsersModal({ open, onClose, onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);       // [{col: value}]
  const [validation, setValidation] = useState(null); // {summary, rows}
  const [tag, setTag] = useState("existing_user");
  const [batchNotes, setBatchNotes] = useState("");
  const [onDuplicate, setOnDuplicate] = useState("skip");
  const [markDnd, setMarkDnd] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // Data-quality context computed at parse time — kept in state so we can
  // surface it in the review UI ("we ignored these columns", "we dropped N
  // deleted rows", "score columns detected", etc.).
  const [importContext, setImportContext] = useState({
    deletedRowsSkipped: 0,
    deletedRowsDetails: [],  // [{row, name, email, phone}] — captured client-side for the Done screen
    ignoredColumns: [],   // headers present in the file but not mapped to our schema
    scoreColumns: [],     // exam-score column headers (used for exam detection)
    rawColumnCount: 0,
  });
  const fileInputRef = useRef(null);

  const reset = () => {
    setStep(1); setFile(null); setRows([]); setValidation(null);
    setTag("existing_user"); setBatchNotes(""); setOnDuplicate("skip");
    setMarkDnd(true); setBusy(false); setResult(null);
    setImportContext({ deletedRowsSkipped: 0, deletedRowsDetails: [], ignoredColumns: [], scoreColumns: [], rawColumnCount: 0 });
  };
  const close = () => { reset(); onClose(); };

  const handleFile = async (f) => {
    if (!f) return;
    setFile(f);
    setBusy(true);
    try {
      const parsed = await parseFile(f);
      // Discover every column header present in the file, then classify:
      //  - "known"       → mapped via COLUMN_ALIASES (we consume it)
      //  - "score"       → matches "Score: N/M ..." pattern (used for exam detection, then dropped)
      //  - "ignored"     → present in the file but never used
      const allKeys = Array.from(new Set(parsed.flatMap((r) => Object.keys(r))));
      const scoreColumns = allKeys.filter((k) => parsed.some((r) => typeof r[k] === "string" && /score\s*:\s*\d+\s*\/\s*\d+/i.test(r[k])));
      const ignoredColumns = allKeys.filter((k) => !KNOWN_COLUMN_KEYS.has(k) && !scoreColumns.includes(k));

      // v29.2 — Deleted users are NO LONGER skipped client-side. They are
      // passed through to the backend which routes them into the "Deleted
      // Users" group with DND + subscription_status="deleted". We still
      // *detect* them here so the UI can surface an informational banner.
      const deletedRowsDetails = [];
      parsed.forEach((r, rowIdx) => {
        if (isDeletedFlag(r.is_delete)) {
          const fn = String(r.first_name ?? "").trim();
          const ln = String(r.last_name ?? "").trim();
          const name = String(r.name ?? "").trim() || `${fn} ${ln}`.trim() || "(no name)";
          deletedRowsDetails.push({
            row: rowIdx + 1,
            name,
            email: String(r.email ?? "").trim(),
            phone: String(r.phone_no ?? r.phone ?? "").trim(),
            reason: "marked_deleted_in_source",
            detail: `is_delete = ${String(r.is_delete)}${r.delete_reason ? ` — ${r.delete_reason}` : ""}`,
          });
        }
      });

      const normalized = parsed.map((r) => toImportRow(r, allKeys));
      setImportContext({
        deletedRowsSkipped: deletedRowsDetails.length,  // kept for compat; now "detected", not "skipped"
        deletedRowsDetails,
        ignoredColumns,
        scoreColumns,
        rawColumnCount: allKeys.length,
      });

      // Immediately validate
      const r = await api.post("/user-import/validate", { rows: normalized });
      // Sync backend auto-classification (trial→expired) into the editable
      // client-side rows so the operator sees what will actually be saved.
      const synced = normalized.map((row, i) => {
        const backendSub = r.data.rows?.[i]?.data?.subscription_status;
        return backendSub && backendSub !== row.subscription_status
          ? { ...row, subscription_status: backendSub }
          : row;
      });
      setRows(synced);
      setValidation(r.data);
      setStep(2);
    } catch (err) {
      toast.error(`Parse failed: ${err.message || err}`);
      setFile(null);
    } finally {
      setBusy(false);
    }
  };

  const revalidate = async (newRows) => {
    setBusy(true);
    try {
      const r = await api.post("/user-import/validate", { rows: newRows });
      setValidation(r.data);
      // Sync the backend's auto-classified subscription_status back into the
      // editable rows so the operator sees what will actually be saved (e.g.
      // "trial" → "expired" for users joined > 3 days ago). Any manual edit
      // afterwards still wins via the input's onChange.
      setRows((prev) => prev.map((row, i) => {
        const backendSub = r.data.rows?.[i]?.data?.subscription_status;
        return backendSub && backendSub !== row.subscription_status
          ? { ...row, subscription_status: backendSub }
          : row;
      }));
    } catch {
      toast.error("Re-validation failed");
    } finally {
      setBusy(false);
    }
  };

  /** Edit a specific cell in a row and re-validate. */
  const editCell = async (rowIdx, col, value) => {
    const newRows = [...rows];
    newRows[rowIdx] = { ...newRows[rowIdx], [col]: value };
    setRows(newRows);
    await revalidate(newRows);
  };

  const commit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/user-import/commit", {
        rows, tag, notes: batchNotes, on_duplicate: onDuplicate, mark_dnd: markDnd,
      });
      setResult(r.data);
      setStep(4);
      toast.success(`Import complete — ${r.data.created} created, ${r.data.updated} updated`);
      onImported?.(r.data);
    } catch (err) {
      toast.error(`Import failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setBusy(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/user-import/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "trial_users_import_template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Template download failed");
    }
  };

  const s = validation?.summary || { ready: 0, needs_edit: 0, duplicate: 0, error: 0, total: 0 };
  const canProceed = useMemo(() => (s.ready + s.duplicate) > 0 && s.needs_edit === 0 && s.error === 0, [s]);

  if (!open) return null;

  return (
    <div data-testid="import-modal" className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={close}>
      <div
        className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <Users className="h-5 w-5 text-orange-600" />
            <h3 className="font-heading text-lg font-bold tracking-tight">Import trial users</h3>
            <div className="flex items-center gap-1 ml-4">
              {["Upload", "Review & fix", "Options", "Done"].map((label, i) => (
                <div key={label} className={`flex items-center gap-1 text-[11px] font-semibold ${step === i + 1 ? "text-orange-600" : step > i + 1 ? "text-slate-500" : "text-slate-300"}`}>
                  <span className={`h-5 w-5 inline-flex items-center justify-center rounded-full border ${step === i + 1 ? "bg-orange-100 border-orange-400" : step > i + 1 ? "bg-slate-100 border-slate-300" : "border-slate-200"}`}>
                    {step > i + 1 ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                  </span>
                  {label}
                  {i < 3 && <ChevronRight className="h-3 w-3 text-slate-300" />}
                </div>
              ))}
            </div>
          </div>
          <button onClick={close}><X className="h-5 w-5 text-slate-500" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto px-6 py-5">
          {/* STEP 1 — Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="text-sm text-slate-600 leading-relaxed">
                Import existing users from a spreadsheet — CSV or Excel (.xlsx). We&apos;ll parse the file, show any rows that need fixing, and only import the ones you approve. <b>No data is written until you click Import on the final step.</b>
              </div>

              <div className="flex items-center justify-between p-4 rounded-md bg-slate-50 border border-slate-200">
                <div className="flex items-center gap-3 text-sm">
                  <Download className="h-4 w-4 text-orange-600" />
                  <div>
                    <div className="font-semibold text-slate-800">Not sure about the format?</div>
                    <div className="text-xs text-slate-500">Download the CSV template with an example row.</div>
                  </div>
                </div>
                <button
                  data-testid="import-download-template"
                  onClick={downloadTemplate}
                  className="text-xs font-semibold px-3 py-2 border border-slate-200 rounded-md hover:bg-white transition-colors duration-150"
                >
                  Download template
                </button>
              </div>

              <button
                data-testid="import-file-picker"
                onClick={() => fileInputRef.current?.click()}
                className="w-full border-2 border-dashed border-slate-300 rounded-lg p-10 text-center hover:border-orange-400 hover:bg-orange-50/30 transition-colors duration-150 group"
              >
                <FileSpreadsheet className="h-10 w-10 text-slate-400 group-hover:text-orange-500 mx-auto mb-3" />
                <div className="text-sm font-semibold text-slate-700 group-hover:text-orange-700">Click to pick a CSV / XLSX file</div>
                <div className="text-xs text-slate-500 mt-1">Max ~5,000 rows recommended per import</div>
              </button>
              <input
                type="file" ref={fileInputRef} className="hidden"
                accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />

              {busy && (
                <div className="text-center text-sm text-slate-500 flex items-center justify-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Parsing file & validating…
                </div>
              )}

              <div className="text-xs text-slate-500 leading-relaxed space-y-1">
                <div><b>Required:</b> <code className="font-mono bg-slate-100 px-1 rounded">name</code> (or <code className="font-mono bg-slate-100 px-1 rounded">first_name</code> + <code className="font-mono bg-slate-100 px-1 rounded">last_name</code>), either <code className="font-mono bg-slate-100 px-1 rounded">email</code> or <code className="font-mono bg-slate-100 px-1 rounded">phone</code>, and <code className="font-mono bg-slate-100 px-1 rounded">exam</code>.</div>
                <div><b>Optional:</b> country, state, city, join_date, last_login, subscription_status, external_id, tags (comma-separated), notes.</div>
                <div className="text-slate-400"><b>Smart-mapping:</b> we auto-detect common export headers like <code className="font-mono bg-slate-100 px-0.5 rounded">phone_no</code>, <code className="font-mono bg-slate-100 px-0.5 rounded">targeting_exam</code>, <code className="font-mono bg-slate-100 px-0.5 rounded">user_code</code>, <code className="font-mono bg-slate-100 px-0.5 rounded">is_subscription</code>, <code className="font-mono bg-slate-100 px-0.5 rounded">joining_date</code>, etc.</div>
              </div>

              <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 leading-relaxed space-y-2">
                <div className="font-semibold text-slate-700">Not imported (data we intentionally ignore)</div>
                <ul className="list-disc pl-5 text-slate-600 space-y-0.5">
                  <li><code className="font-mono bg-white px-1 rounded">address</code>, <code className="font-mono bg-white px-1 rounded">pin_code</code>, <code className="font-mono bg-white px-1 rounded">year_apearance</code> — not part of Connect&apos;s schema.</li>
                  <li><code className="font-mono bg-white px-1 rounded">is_delete</code>, <code className="font-mono bg-white px-1 rounded">delete_reason</code> — flag deleted users (routed to Deleted Users group with DND).</li>
                  <li><code className="font-mono bg-white px-1 rounded">subscription_exam</code> and per-exam score columns (e.g. <code className="font-mono bg-white px-1 rounded">NCLEX - RN</code>) — used only to auto-detect the active exam (score &gt; 10), then dropped.</li>
                  <li>Date fields containing <code className="font-mono bg-white px-1 rounded">Never logged in</code>, <code className="font-mono bg-white px-1 rounded">N/A</code>, or <code className="font-mono bg-white px-1 rounded">7 months ago</code> — treated as blank.</li>
                </ul>
                <div className="font-semibold text-slate-700 pt-1">Automatic classification</div>
                <ul className="list-disc pl-5 text-slate-600 space-y-0.5">
                  <li>Users who joined more than <b>3 days ago</b> → <code className="font-mono bg-white px-1 rounded">expired</code>. Newer → <code className="font-mono bg-white px-1 rounded">trial</code>.</li>
                  <li>Phone numbers without a country code get tagged <code className="font-mono bg-white px-1 rounded">phone_incomplete</code> so WhatsApp stays disabled until you fix them.</li>
                  <li>Every user gets the blanket tag <code className="font-mono bg-white px-1 rounded">imported</code> plus a batch tag like <code className="font-mono bg-white px-1 rounded">import_batch_20260217_...</code> for filtering / rollback.</li>
                </ul>
              </div>
            </div>
          )}

          {/* STEP 2 — Review & Fix */}
          {step === 2 && validation && (
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <StatSummary label="Total"      value={s.total}      cls="bg-slate-50 border-slate-200 text-slate-700" />
                <StatSummary label="Ready"      value={s.ready}      cls="bg-emerald-50 border-emerald-200 text-emerald-700" />
                <StatSummary label="Needs fix" value={s.needs_edit} cls="bg-amber-50 border-amber-200 text-amber-800" />
                <StatSummary label="Duplicate" value={s.duplicate}  cls="bg-blue-50 border-blue-200 text-blue-700" />
                <StatSummary label="Error"     value={s.error}      cls="bg-red-50 border-red-200 text-red-700" />
                {busy && <span className="text-xs text-slate-500 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Re-validating…</span>}
              </div>

              {/* Post-classification breakdown — trial vs expired vs data-quality flags. */}
              {(() => {
                const trial = validation.rows.filter((r) => r.data?.subscription_status === "trial").length;
                const expired = validation.rows.filter((r) => r.data?.subscription_status === "expired").length;
                const paid = validation.rows.filter((r) => r.data?.subscription_status === "paid").length;
                const phoneIncomplete = validation.rows.filter((r) => r.data?._hints?.phone_incomplete).length;
                const examUnknown = validation.rows.filter((r) => r.data?._hints?.exam_unknown).length;
                return (
                  <div className="flex items-center gap-2 flex-wrap text-xs">
                    <span className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Auto-classified:</span>
                    <StatSummary label="In trial"       value={trial}   cls="bg-orange-50 border-orange-200 text-orange-700" />
                    <StatSummary label="Trial expired" value={expired} cls="bg-slate-100 border-slate-300 text-slate-700" />
                    {paid > 0 && <StatSummary label="Paid" value={paid} cls="bg-emerald-50 border-emerald-200 text-emerald-700" />}
                    {phoneIncomplete > 0 && <StatSummary label="Phone incomplete" value={phoneIncomplete} cls="bg-amber-50 border-amber-200 text-amber-800" />}
                    {examUnknown > 0 && <StatSummary label="Exam unknown" value={examUnknown} cls="bg-slate-50 border-slate-200 text-slate-500" />}
                  </div>
                );
              })()}

              {/* File-quality context — what we skipped and ignored during parse. */}
              {(importContext.deletedRowsSkipped > 0 || importContext.ignoredColumns.length > 0 || importContext.scoreColumns.length > 0) && (
                <div className="text-xs bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1.5">
                  {importContext.deletedRowsSkipped > 0 && (
                    <div className="text-amber-800 bg-amber-50 -mx-3 -mt-3 px-3 py-2 border-b border-amber-200 rounded-t-md">
                      Detected <b>{importContext.deletedRowsSkipped}</b> deleted user{importContext.deletedRowsSkipped === 1 ? "" : "s"} (<code className="font-mono bg-white px-1 rounded">is_delete = Yes</code>) — they <b>will</b> be imported with <code className="font-mono bg-white px-1 rounded">subscription_status = deleted</code>, marked <b>DND</b>, and added to the <b>Deleted Users</b> group.
                    </div>
                  )}
                  {importContext.scoreColumns.length > 0 && (
                    <div className="text-slate-700">
                      Detected <b>{importContext.scoreColumns.length}</b> exam-score column{importContext.scoreColumns.length === 1 ? "" : "s"} ({importContext.scoreColumns.slice(0, 3).map((c) => <code key={c} className="font-mono bg-white px-1 rounded mr-1">{c}</code>)}
                      {importContext.scoreColumns.length > 3 && <span>+{importContext.scoreColumns.length - 3} more</span>}) — used to detect active exam (score &gt; 10), not stored.
                    </div>
                  )}
                  {importContext.ignoredColumns.length > 0 && (
                    <div className="text-slate-500">
                      Ignored columns not used by Connect: {importContext.ignoredColumns.map((c) => <code key={c} className="font-mono bg-white px-1 rounded mr-1">{c}</code>)}
                    </div>
                  )}
                </div>
              )}

              {s.needs_edit > 0 && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                  <b>{s.needs_edit} row(s) need fixing</b> before you can proceed. Click a red-highlighted cell to fix inline.
                </div>
              )}

              <div className="border border-slate-200 rounded-md overflow-hidden">
                <div className="max-h-[400px] overflow-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="text-left px-2 py-2 w-10">#</th>
                        <th className="text-left px-2 py-2 w-24">Status</th>
                        {REQUIRED_COLS.map((c) => (
                          <th key={c} className="text-left px-2 py-2 font-semibold text-slate-700">{c}<span className="text-red-500">*</span></th>
                        ))}
                        {["country", "state", "city", "join_date", "last_login", "subscription_status", "external_id"].map((c) => (
                          <th key={c} className="text-left px-2 py-2 font-semibold text-slate-500">{c}</th>
                        ))}
                        <th className="text-left px-2 py-2 font-semibold text-slate-500" title="Validation issues and duplicate matches for this row">Issues</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.rows.map((r, i) => {
                        const issuesByField = Object.fromEntries((r.issues || []).map((x) => [x.field, x.message]));
                        const hints = r.data?._hints || {};
                        return (
                          <tr key={i} className={`border-t border-slate-100 ${r.status === "needs_edit" ? "bg-amber-50/30" : r.status === "duplicate" ? "bg-blue-50/20" : ""}`}>
                            <td className="px-2 py-1.5 text-slate-500 font-mono tabular">{i + 1}</td>
                            <td className="px-2 py-1.5 space-y-0.5">
                              <StatusPill status={r.status} />
                              {hints.auto_classified && (
                                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Trial expired</div>
                              )}
                              {hints.phone_incomplete && (
                                <div className="text-[9px] uppercase tracking-widest text-amber-700 font-semibold">Phone incomplete</div>
                              )}
                              {hints.exam_unknown && (
                                <div className="text-[9px] uppercase tracking-widest text-slate-500 font-semibold">Exam unknown</div>
                              )}
                            </td>
                            {[...REQUIRED_COLS, "country", "state", "city", "join_date", "last_login", "subscription_status", "external_id"].map((col) => {
                              const hasIssue = !!issuesByField[col];
                              const val = rows[i]?.[col] ?? "";
                              return (
                                <td key={col} className={`px-1 py-1 ${hasIssue ? "bg-red-50" : ""}`}>
                                  <input
                                    className={`w-full px-1.5 py-1 text-xs border rounded bg-white ${hasIssue ? "border-red-300 focus:border-red-500" : "border-transparent hover:border-slate-200 focus:border-orange-400"} outline-none`}
                                    value={val}
                                    title={val}
                                    onChange={(e) => {
                                      const newRows = [...rows];
                                      newRows[i] = { ...newRows[i], [col]: e.target.value };
                                      setRows(newRows);
                                    }}
                                    onBlur={() => revalidate(rows)}
                                    placeholder={col === "join_date" ? "YYYY-MM-DD" : col === "subscription_status" ? "trial|expired|paid|cancelled" : ""}
                                  />
                                </td>
                              );
                            })}
                            <td className="px-2 py-1.5 text-[10px] leading-tight">
                              {(r.issues || []).length > 0 && (
                                <div className="text-red-700">{(r.issues || []).map((x) => x.message).join(" · ")}</div>
                              )}
                              {r.status === "duplicate" && r.existing && (
                                <div className="text-blue-700">
                                  Matches: {r.existing.name} ({r.existing.email || r.existing.phone})
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3 — Options */}
          {step === 3 && (
            <div className="space-y-5 max-w-2xl">
              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Tags to apply to every imported user</label>
                <input
                  data-testid="import-tag"
                  value={tag} onChange={(e) => setTag(e.target.value)}
                  placeholder="existing_user, jul_2025_signups, webinar_batch"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none font-mono"
                />
                <div className="text-[11px] text-slate-500 mt-1">Separate multiple tags with commas. Each becomes a filterable label. Example: <code className="font-mono bg-slate-100 px-1 rounded">/users?tag=your_tag</code></div>
                {(tag || "").trim() && (
                  <div className="flex items-center gap-1.5 flex-wrap mt-2">
                    {tag.split(",").map((t) => t.trim()).filter(Boolean).map((t, i) => (
                      <span key={i} className="text-[11px] font-mono bg-orange-50 text-orange-700 border border-orange-200 rounded px-2 py-0.5">{t}</span>
                    ))}
                    <span className="text-[11px] font-mono bg-slate-100 text-slate-600 border border-slate-200 rounded px-2 py-0.5">imported</span>
                    <span className="text-[11px] font-mono bg-slate-100 text-slate-600 border border-slate-200 rounded px-2 py-0.5">import_batch_...</span>
                  </div>
                )}
              </div>

              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">Note for this batch (added to every imported user)</label>
                <textarea
                  data-testid="import-notes"
                  rows={3}
                  value={batchNotes} onChange={(e) => setBatchNotes(e.target.value)}
                  placeholder="e.g. Imported from Feb 2026 webinar signup list."
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md bg-white focus:border-orange-500 outline-none"
                />
              </div>

              <div>
                <label className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 block mb-1">If a matching user already exists…</label>
                <div className="flex gap-3">
                  {[
                    { v: "skip",   t: "Skip",   d: "Leave existing user untouched" },
                    { v: "update", t: "Update", d: "Merge new tags + notes into the existing record" },
                  ].map((opt) => (
                    <label key={opt.v} className={`flex-1 cursor-pointer border rounded-md px-3 py-2 ${onDuplicate === opt.v ? "border-orange-500 bg-orange-50" : "border-slate-200 hover:border-slate-300"}`}>
                      <input type="radio" name="dup" checked={onDuplicate === opt.v} onChange={() => setOnDuplicate(opt.v)} className="mr-2" />
                      <span className="text-sm font-semibold">{opt.t}</span>
                      <div className="text-[11px] text-slate-500">{opt.d}</div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="p-3 rounded-md bg-amber-50 border border-amber-200">
                <label className="flex items-start gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={markDnd} onChange={(e) => setMarkDnd(e.target.checked)} className="mt-0.5" />
                  <div>
                    <div className="font-semibold text-amber-900">Mark as DND until consent is confirmed (recommended)</div>
                    <div className="text-[11px] text-amber-800 leading-relaxed mt-0.5">
                      WhatsApp Business rules require opt-in. Imported users will start with <code className="font-mono bg-amber-100 px-1 rounded">dnd=true</code> so no proactive messages fire. Flip DND off per-user only when you&apos;ve confirmed their consent.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* STEP 4 — Done */}
          {step === 4 && result && (
            <div className="space-y-4 max-w-2xl">
              <div className="text-center py-6">
                <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto mb-2" />
                <h3 className="font-heading text-xl font-bold tracking-tight">Import complete</h3>
                <div className="text-sm text-slate-600 mt-1">Batch <code className="font-mono bg-slate-100 px-1 rounded">{result.batch_tag}</code></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-emerald-200 bg-emerald-50 rounded-md p-3 text-center"><div className="text-2xl font-bold text-emerald-700 tabular">{result.created}</div><div className="text-[11px] uppercase tracking-widest text-emerald-700">Created</div></div>
                <div className="border border-blue-200 bg-blue-50 rounded-md p-3 text-center"><div className="text-2xl font-bold text-blue-700 tabular">{result.updated}</div><div className="text-[11px] uppercase tracking-widest text-blue-700">Updated</div></div>
                <div className="border border-slate-200 bg-slate-50 rounded-md p-3 text-center"><div className="text-2xl font-bold text-slate-700 tabular">{result.skipped}</div><div className="text-[11px] uppercase tracking-widest text-slate-600">Skipped</div></div>
              </div>

              {(result.trial > 0 || result.expired > 0 || result.paid > 0) && (
                <div className="border border-slate-200 rounded-md p-3">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Auto-classification</div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {result.trial > 0 && <span className="text-xs bg-orange-50 text-orange-700 border border-orange-200 px-2 py-0.5 rounded">In trial: <b>{result.trial}</b></span>}
                    {result.expired > 0 && <span className="text-xs bg-slate-100 text-slate-700 border border-slate-300 px-2 py-0.5 rounded">Trial expired: <b>{result.expired}</b></span>}
                    {result.paid > 0 && <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded">Paid: <b>{result.paid}</b></span>}
                    {result.phone_incomplete > 0 && <span className="text-xs bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5 rounded">Phone incomplete: <b>{result.phone_incomplete}</b></span>}
                    {result.exam_unknown > 0 && <span className="text-xs bg-slate-50 text-slate-500 border border-slate-200 px-2 py-0.5 rounded">Exam unknown: <b>{result.exam_unknown}</b></span>}
                  </div>
                </div>
              )}

              <div className="border border-slate-200 rounded-md p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Filter these users in Connect</div>
                <div className="flex flex-col gap-1.5 text-xs">
                  <a href={`/users?tag=${encodeURIComponent(result.batch_tag)}`} className="text-orange-600 font-semibold hover:underline">
                    → This batch only ({result.batch_tag})
                  </a>
                  <a href="/users?tag=imported" className="text-orange-600 font-semibold hover:underline">
                    → Every imported user (tag: imported)
                  </a>
                  {result.phone_incomplete > 0 && (
                    <a href="/users?tag=phone_incomplete" className="text-amber-700 font-semibold hover:underline">
                      → Users needing phone cleanup ({result.phone_incomplete})
                    </a>
                  )}
                  {result.exam_unknown > 0 && (
                    <a href="/users?tag=exam_unknown" className="text-slate-600 font-semibold hover:underline">
                      → Users needing exam assignment ({result.exam_unknown})
                    </a>
                  )}
                </div>
                <div className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                  All imported users start with <code className="font-mono bg-slate-100 px-1 rounded">dnd=true</code> — no messages will be sent until you flip DND off per user (or in bulk).
                </div>
              </div>

              {result.errors?.length > 0 && (
                <div className="text-xs text-red-800 bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-auto">
                  {result.errors.map((e, i) => (
                    <div key={i}>Row {e.row + 1}: {e.reason}</div>
                  ))}
                </div>
              )}

              {/* Skipped users — backend skips only (duplicates, validation-failed).
                  v29.2: `deletedRowsDetails` are NO LONGER included here — they're
                  imported (into Deleted Users group), not skipped. */}
              {(() => {
                const allSkipped = result.skipped_details || [];
                if (allSkipped.length === 0) return null;
                return (
                  <details className="border border-slate-200 rounded-md">
                    <summary data-testid="skipped-users-toggle" className="cursor-pointer px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                      Skipped users — {allSkipped.length} not imported (click to expand)
                    </summary>
                    <div className="max-h-64 overflow-auto border-t border-slate-200">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 sticky top-0">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-500 w-10">#</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Name</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Contact</th>
                            <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {allSkipped.map((s, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="px-2 py-1.5 font-mono tabular text-slate-500">{s.row}</td>
                              <td className="px-2 py-1.5 text-slate-800">{s.name || "(no name)"}</td>
                              <td className="px-2 py-1.5 text-slate-600 truncate max-w-[200px]" title={`${s.email} · ${s.phone}`}>
                                {s.email || s.phone || "—"}
                              </td>
                              <td className="px-2 py-1.5">
                                <span className={`inline-block text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 rounded border ${
                                  s.reason === "duplicate_existing_user"      ? "bg-blue-50 text-blue-700 border-blue-200" :
                                  s.reason === "validation_failed"            ? "bg-amber-50 text-amber-800 border-amber-200" :
                                  s.reason === "marked_deleted_in_source"     ? "bg-slate-100 text-slate-700 border-slate-300" :
                                  "bg-red-50 text-red-700 border-red-200"
                                }`}>
                                  {s.reason.replace(/_/g, " ")}
                                </span>
                                {s.detail && <div className="text-[10px] text-slate-500 leading-tight mt-0.5">{s.detail}</div>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                );
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-6 py-3 flex items-center justify-between">
          <div className="text-[11px] text-slate-400">
            {file && `${file.name} · ${rows.length} rows`}
          </div>
          <div className="flex items-center gap-2">
            {step > 1 && step < 4 && (
              <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded border border-slate-200 hover:bg-slate-50">
                <ChevronLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            {step === 1 && <button onClick={close} className="text-xs px-3 py-2 rounded border border-slate-200 hover:bg-slate-50">Cancel</button>}
            {step === 2 && (
              <button
                data-testid="import-next-btn"
                onClick={() => setStep(3)} disabled={!canProceed || busy}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded bg-orange-600 text-white hover:bg-orange-500 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {canProceed ? "Continue" : "Fix all rows first"} <ChevronRight className="h-3.5 w-3.5" />
              </button>
            )}
            {step === 3 && (
              <button
                data-testid="import-commit-btn"
                onClick={commit} disabled={busy}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded bg-orange-600 text-white hover:bg-orange-500 transition-colors duration-150 disabled:opacity-60"
              >
                {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Importing…</> : <><Upload className="h-3.5 w-3.5" /> Import {s.ready + (onDuplicate === "update" ? s.duplicate : 0)} users</>}
              </button>
            )}
            {step === 4 && (
              <button onClick={close} className="text-xs font-semibold px-3 py-2 rounded bg-orange-600 text-white hover:bg-orange-500 transition-colors duration-150">Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatSummary({ label, value, cls }) {
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded border text-xs ${cls}`}>
      <span className="font-mono tabular font-bold">{value}</span>
      <span className="text-[10px] uppercase tracking-widest opacity-80">{label}</span>
    </div>
  );
}
